/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
/* eslint no-unused-vars: ["error", { "varsIgnorePattern": "[iI]gnored" }] */

import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grow from '@material-ui/core/Grow'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import SearchIcon from '@material-ui/icons/Search'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import RoomIcon from '@material-ui/icons/Room'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import IconButton from '@material-ui/core/IconButton'
import ClearIcon from '@material-ui/icons/Clear'
import TuneIcon from '@material-ui/icons/Tune'
import { useRouter } from 'next/router'
import { IconMarker } from '@components/atoms/icons'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { autocompleteCity } from '@lib/geo-api'
import Link from 'next/link'
import { useStyles } from './places.styles'

const PlacesRefiner = ({
  setAroundLatLng,
  placeSearchValue,
  setPlaceSearchValue,
  setLoadingLocationGtm,
  showMapView,
  setShowMapView,
  showRefiners,
  setShowRefiners,
}) => {
  const [searchResults, setSearchResults] = useState([])
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false)
  const placesSearchInputRef = useRef()
  const styles = useStyles()
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width:959.9px)')
  const searchInputRef = useRef()
  const btnRef = useRef()

  const performSearch = async (query) => {
    const citiesAutocompleteResult = await autocompleteCity(query)
    const cities = citiesAutocompleteResult.map(item => (
      {
        id: item.objectID,
        cityName: item.city,
        stateName: item.state,
        lat: item._geoloc.lat,
        lng: item._geoloc.lng,
      }
    ))
    setSearchResults(cities)
    setIsSearchDropdownOpen(true)
  }

  const onSearchInput = (e) => {
    setLoadingLocationGtm(true)
    setPlaceSearchValue(e.target.value)
    performSearch(e.target.value || null)
  }

  const onSearchClear = () => {
    const path = window.location.pathname
    const searchPath = '/things-to-do/search'

    if (window && path !== searchPath) {
      return router.push(`${searchPath}?reset=all`)
    }
    setPlaceSearchValue('')
    setSearchResults([])
    setAroundLatLng('')
    setIsSearchDropdownOpen(false)
    placesSearchInputRef.current.querySelector('input').focus()
    return null
  }

  const redirectToLatLng = (latitude, longitude) => {
    return window.location.assign(`${window.location.origin}/things-to-do/search?aroundLatLng=${latitude}, ${longitude}`)
  }

  const onSelectSearchResult = async (cityName, stateName, lat, lng) => {
    const cityState = `${cityName}, ${stateName}`
    const dataLayer = window.dataLayer || []
    dataLayer.push({
      event: 'GroupEventsSearchLocation',
      searchLocationValue: cityState,
    })
    return redirectToLatLng(lat, lng)
  }

  const locationSuccess = (position) => {
    const { latitude } = position.coords
    const { longitude } = position.coords
    redirectToLatLng(latitude, longitude)
  }

  // redirect to current position results on /things-to-do/search first load
  useEffect(() => {
    if (
      navigator
      && window.navigator.geolocation
      && isEmpty(router.query)
      && window.history.length < 3) {
      navigator.geolocation.getCurrentPosition(locationSuccess)
    }
  }, [])

  const navigateToLocationSearch = (cityName, stateName) => {
    router.push(encodeURI(`/things-to-do/${stateName}/${cityName}`).toLowerCase())
  }

  const handleKeyUp = (event) => {
    if (event.key === 'Tab' && event.target.value) {
      btnRef.current.focus()
    }
    if (event.key === 'Enter' && event.target.value) {
      event.preventDefault()
      if (searchResults.length === 0) return
      navigateToLocationSearch(searchResults[0].cityName, searchResults[0].stateName)
    }
  }

  return (
    <Grid
      container
      className={styles.container}
      display="flex"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid item xs={1} className={styles.mobileSecNavContainer}>
        <Link href="/things-to-do" passHref>
          <IconButton className={styles.mobileSecNavBtn}>
            <ArrowBackIcon className={styles.mobileSecNavIcons} />
          </IconButton>
        </Link>
      </Grid>
      <Grid
        item
        xs={isMobile ? 8 : 11}
        data-cy="ttd-search-box"
      >
        <TextField
          fullWidth
          variant="outlined"
          className={styles.searchInput}
          label="Search by city"
          autoFocus
          value={placeSearchValue}
          onChange={onSearchInput}
          onKeyUp={handleKeyUp}
          ref={placesSearchInputRef}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <SearchIcon className={styles.searchIcon} />
            ),
            endAdornment: (
              placeSearchValue ? (
                <IconButton onClick={onSearchClear}>
                  <ClearIcon />
                </IconButton>
              ) : null
            ),
          }}
        />
      </Grid>
      {isMobile && (
        <>
          <Grid
            item
            xs={1}
            className={styles.mobileSecNavContainer}
            data-cy="ttd-filters-switcher"
          >
            <IconButton
              className={styles.mobileSecNavBtn}
              onClick={() => { setShowRefiners(!showRefiners) }}
            >
              <TuneIcon className={styles.mobileSecNavIcons} />
            </IconButton>
          </Grid>
          <Grid
            item
            xs={1}
            className={styles.mobileSecNavContainer}
            data-cy="ttd-map-view-switcher"
          >
            <IconButton
              className={styles.mobileSecNavBtn}
              onClick={() => { setShowMapView(!showMapView) }}
            >
              {showMapView
                ? <FormatListBulletedIcon className={styles.mobileSecNavIcons} />
                : <RoomIcon className={styles.mobileSecNavIcons} />
              }
            </IconButton>
          </Grid>
        </>
      )}
      <Popper open={isSearchDropdownOpen} className={styles.dropdownRoot} anchorEl={placesSearchInputRef.current} role={undefined} placement="bottom" transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper style={{ width: placesSearchInputRef.current.offsetWidth }}>
              <ClickAwayListener onClickAway={() => setIsSearchDropdownOpen(false)}>
                <Grid container>
                  <Grid item xs={12}>
                    {searchResults.map(({
                      cityName, stateName, id, lat, lng,
                    }) => {
                      const isSelected = searchResults[0].id === id
                      return (
                        <Button
                          key={`${cityName}, ${stateName}`}
                          className={`${styles.locationSearchResultButton} ${isSelected && document.activeElement === searchInputRef.current ? styles.locationSearchResultButtonActive : ''}`}
                          startIcon={<IconMarker className={styles.locationSearchResultIcon} />}
                          onClick={() => { onSelectSearchResult(cityName, stateName, lat, lng) }}
                          ref={isSelected ? btnRef : null}
                        >
                          {cityName}, {stateName}
                        </Button>
                      )
                    })}
                    {searchResults.length === 0 && (
                      <Box p={2}>
                        <Typography variant="p">No results found for &quot;{placeSearchValue}&quot;</Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Grid>
  )
}

PlacesRefiner.propTypes = {
  setAroundLatLng: PropTypes.func,
  placeSearchValue: PropTypes.string,
  setPlaceSearchValue: PropTypes.func,
  setLoadingLocationGtm: PropTypes.func,
  showMapView: PropTypes.bool,
  setShowMapView: PropTypes.func,
  showRefiners: PropTypes.bool,
  setShowRefiners: PropTypes.func,
}

export default PlacesRefiner
